import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  MapPin,
  Mail,
  Phone,
  Facebook,
  Twitter,
  Instagram,
} from "lucide-react";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleScrollToRoutes = () => {
    // If we are on the home page, scroll to section
    if (location.pathname === "/") {
      const element = document.getElementById("routes-section");
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  return (
    <footer className="relative bg-gray-900 border-t border-white/10 text-white py-12 shadow-[0_-10px_40px_rgba(0,0,0,0.3)] dark:shadow-[0_-10px_40px_rgba(255,255,255,0.05)] z-40">
      <div className="container mx-auto px-6">
        <div className="grid md:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <div className="flex items-center space-x-3 mb-4">
              <Link
                to="/"
                onClick={handleScrollToTop}
                className="flex-shrink-0"
              >
                <img
                  src="/KOSET-LOGO-WITH-TM-1.png"
                  alt="Koset Logo"
                  className="h-12 w-auto object-contain"
                />
              </Link>
              <span className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-400">
                Koset
              </span>
            </div>
            <p className="text-gray-400 mb-6 leading-relaxed">
              Discover verified cycling routes across India. Join our community
              of passionate cyclists.
            </p>

            {/* Legal Links (New Requirement) */}
            <div className="flex flex-col space-y-2 mb-6">
              <Link
                to="/terms"
                className="text-gray-400 hover:text-green-500 text-sm transition-colors w-fit"
              >
                Terms & Conditions
              </Link>
              <Link
                to="/privacy"
                className="text-gray-400 hover:text-green-500 text-sm transition-colors w-fit"
              >
                Privacy Policy
              </Link>
            </div>

            <div className="flex space-x-4">
              {[Facebook, Twitter, Instagram].map((Icon, index) => (
                <motion.a
                  key={index}
                  href="#"
                  whileHover={{ scale: 1.1, color: "#16a34a" }}
                  whileTap={{ scale: 0.95 }}
                  className="text-gray-400 transition-colors"
                >
                  <Icon className="w-5 h-5" />
                </motion.a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-white">
              Quick Links
            </h3>
            <ul className="space-y-3">
              {/* Specialized Links */}
              <li>
                <Link
                  to="/"
                  onClick={handleScrollToTop}
                  className="text-gray-400 hover:text-green-600 transition-all hover:pl-1 inline-block"
                >
                  Home
                </Link>
              </li>
              <li>
                <Link
                  to="/#routes-section"
                  onClick={handleScrollToRoutes}
                  className="text-gray-400 hover:text-green-600 transition-all hover:pl-1 inline-block"
                >
                  Routes
                </Link>
              </li>
              {/* Standard Links */}
              {["Events", "About", "Contact"].map((link) => (
                <li key={link}>
                  <Link
                    to={`/${link.toLowerCase()}`}
                    className="text-gray-400 hover:text-green-600 transition-all hover:pl-1 inline-block"
                  >
                    {link}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Popular Routes */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-white">
              Popular Routes
            </h3>
            <ul className="space-y-3">
              {[
                "Mumbai Coastal",
                "Delhi Heritage",
                "Goa Beaches",
                "Manali Mountains",
              ].map((route) => (
                <li key={route}>
                  <Link
                    to="#"
                    className="text-gray-400 hover:text-green-600 transition-all hover:pl-1 inline-block"
                  >
                    {route}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-white">Contact</h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-3 group cursor-pointer">
                <div className="p-2 bg-gray-800 rounded-lg group-hover:bg-gray-700 transition-colors shrink-0 mt-1">
                  <MapPin className="w-4 h-4 text-green-500" />
                </div>
                <span className="text-gray-400 group-hover:text-white transition-colors leading-relaxed">
                  Hoshiarpur Cyclewalle The Bikestore Purheeran, Phagwara Road,
                  Hoshiarpur – 146001
                </span>
              </div>
              <a
                href="mailto:bharatalacrity@gmail.com"
                className="flex items-center space-x-3 group cursor-pointer"
              >
                <div className="p-2 bg-gray-800 rounded-lg group-hover:bg-gray-700 transition-colors">
                  <Mail className="w-4 h-4 text-green-500" />
                </div>
                <span className="text-gray-400 group-hover:text-white transition-colors">
                  bharatalacrity@gmail.com
                </span>
              </a>
              <div className="flex items-center space-x-3 group cursor-pointer">
                <div className="p-2 bg-gray-800 rounded-lg group-hover:bg-gray-700 transition-colors">
                  <Phone className="w-4 h-4 text-green-500" />
                </div>
                <span className="text-gray-400 group-hover:text-white transition-colors">
                  +91-94630-90468
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-12 pt-8 text-center">
          <p className="text-gray-500 text-sm">
            © {currentYear} Koset. All rights reserved. Made with{" "}
            <span className="text-red-500 animate-pulse">❤️</span> for cyclists.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
